import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  type ButtonProps,
  Drawer,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useBoolean } from 'react-use'

import {
  EmptyState,
  EmptyStateText,
  EmptyStateTitle
} from 'components/common/EmptyState'
import PageSection from 'components/common/PageSection'
import { InvertedPassCriteriaNotification } from 'components/common/Runs/InvertedPassCriteriaNotification'
import { ScenarioPassCriteria } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import { PromotedScenarioPreview } from 'components/test-suites/forms/test-suite-composition/PromotedScenarioPreview'
import { TestSuiteEditDetailsDrawerTitle } from 'components/test-suites/forms/test-suite-composition/TestSuiteTestDetailsDrawerTitle'
import {
  getDefaultValuesFromTestSuiteScenario,
  getNewTestSuiteWithPolicy,
  getNewTestSuiteScenario,
  getScenarioType,
  getTestSuiteTestDetailsValidationSchema,
  POLICY_CATEGORY_OPTIONS,
  TestSuiteScenario,
  TestSuiteTestDetailsFormValues,
  USER_MESSAGES
} from 'components/test-suites/forms/test-suite-form-utils'
import {
  type ScenarioForTestSuite,
  useTestSuiteFormContext
} from 'components/test-suites/forms/TestSuiteFormContext'
import useAuth from 'hooks/use-auth'
import { useGetReliabilityTestPreview } from 'queries/reliability-tests/use-get-reliability-test-preview'
import { type ScenarioPreview } from 'types/api/reliability-tests/preview'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

const DRAWER_WIDTH = 830

/*
 * Valid props configurations:
 * 1. Edit: `testToEdit` is required
 * 2. New: `scenario` is required
 */
export const TestSuiteTestDetailsDrawer: React.FC<
  | {
      buttonProps?: Omit<ButtonProps, 'children'>
      buttonText: string
      handleSubmit: (scenario: TestSuiteScenario) => void
    } & (
      | {
          scenario: ScenarioForTestSuite
          testToEdit?: never
        }
      | {
          scenario?: never
          testToEdit: TestSuiteScenario
        }
    )
> = ({
  buttonProps,
  buttonText,
  scenario: scenarioFromProp,
  testToEdit,
  handleSubmit
}) => {
  const { currentTeamId } = useAuth()
  const { scenariosLookup } = useTestSuiteFormContext()
  const [isDrawerOpen, setIsDrawerOpen] = useBoolean(false)

  const scenario = scenarioFromProp ?? scenariosLookup[testToEdit.scenarioId]
  const isEdit = testToEdit !== undefined
  const isPassCriteriaInverted =
    scenario?.pass_criteria === ScenarioPassCriteria.AT_LEAST_ONE_FAILED

  const defaultValues: TestSuiteTestDetailsFormValues = useMemo(() => {
    return getDefaultValuesFromTestSuiteScenario(testToEdit, scenario)
  }, [testToEdit, scenario])

  const testSuiteTestDetailsValidationSchema =
    getTestSuiteTestDetailsValidationSchema()

  const { control, getValues, formState, reset, watch } =
    useForm<TestSuiteTestDetailsFormValues>({
      defaultValues,
      mode: 'onChange',
      resolver: yupResolver(testSuiteTestDetailsValidationSchema)
    })

  const isDisabled = !formState.isValid || formState.isSubmitting
  const policyCategory = watch('policyCategory')

  const {
    data: scenarioPreview,
    isError: isErrorScenarioPreview,
    isFetching: isFetchingScenarioPreview,
    isSuccess: isSuccessScenarioPreview,
    error: errorScenarioPreview
  } = useGetReliabilityTestPreview(
    scenario
      ? {
          policyCategory,
          scenarioId: scenario.guid,
          scenarioType: getScenarioType(scenario),
          teamId: 'org_id' in scenario ? scenario.org_id : currentTeamId
        }
      : undefined,
    {
      enabled: isDrawerOpen && Boolean(scenario),
      placeholderData: { graph: {} } as ScenarioPreview,
      staleTime: ms.minutes(5)
    }
  )

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
  }

  const onSubmit = () => {
    const formValues = getValues()

    const newTestSuite = isEdit
      ? getNewTestSuiteWithPolicy(formValues, testToEdit)
      : getNewTestSuiteScenario(formValues, testToEdit, scenario)

    handleSubmit(newTestSuite)
    handleCloseDrawer()
  }

  // Re-initialize the form state on scenario change
  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  return (
    <>
      <Button
        aria-expanded={isDrawerOpen}
        aria-haspopup="dialog"
        data-testid="test-suite-scenario-details-button-open"
        size="large"
        {...buttonProps}
        variant="contained"
        onClick={handleOpenDrawer}
      >
        {buttonText}
      </Button>
      <Drawer anchor="right" open={isDrawerOpen} onClose={handleCloseDrawer}>
        <Stack
          spacing={4}
          sx={{ paddingX: 3, paddingY: 4, width: DRAWER_WIDTH }}
        >
          <TestSuiteEditDetailsDrawerTitle isEdit={isEdit}>
            {testToEdit
              ? testToEdit.name
              : scenario?.name ?? 'Unknown Scenario'}
          </TestSuiteEditDetailsDrawerTitle>
          {isPassCriteriaInverted && <InvertedPassCriteriaNotification />}
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid="test-suite-scenario-details-test-name"
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error
                    ? fieldState.error.message
                    : "Teams will see this name when looking at a service's test"
                }
                label="Test Name"
              />
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                multiline
                data-testid="test-suite-scenario-details-description"
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error
                    ? fieldState.error.message
                    : "Teams will see this description on their service's detail page"
                }
                label="Test Description"
              />
            )}
          />
          <Stack spacing={1}>
            <Typography>
              Assign this Scenario a category (Redundancy, Scalability,
              Dependencies, and Other). Dependencies should include network
              tests, which will impact traffic from the service to that
              dependency.
            </Typography>
            <Controller
              control={control}
              name="policyCategory"
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  data-testid="test-suite-scenario-details-policy-category"
                  error={Boolean(fieldState.error)}
                >
                  {POLICY_CATEGORY_OPTIONS.map((policyCategoryOption) => (
                    <MenuItem
                      key={policyCategoryOption.value}
                      data-testid={`test-suite-scenario-details-policy-category-menu-item-${policyCategoryOption.value}`}
                      value={policyCategoryOption.value}
                    >
                      {policyCategoryOption.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              data-testid="test-suite-scenario-details-button-save"
              disabled={isDisabled}
              variant="contained"
              onClick={onSubmit}
            >
              Save
            </Button>
            <Button
              color="secondary"
              data-testid="test-suite-scenario-details-button-cancel"
              variant="contained"
              onClick={handleCloseDrawer}
            >
              Cancel
            </Button>
          </Stack>

          {scenario ? (
            <Box>
              <Typography variant="h5">Test Preview</Typography>
              <PageSection
                errorMessage={messageFromResponseError(
                  errorScenarioPreview,
                  'There was an issue fetching the preview for this test. Please try again later.'
                )}
                hasError={isErrorScenarioPreview}
                isLoading={isFetchingScenarioPreview}
                isLoadingOverlayFullScreen={false}
              >
                {isSuccessScenarioPreview && (
                  <PromotedScenarioPreview
                    scenario={scenario}
                    scenarioPreview={scenarioPreview}
                  />
                )}
              </PageSection>
            </Box>
          ) : (
            <EmptyState>
              <EmptyStateTitle>Test preview unavailable</EmptyStateTitle>
              <EmptyStateText>
                {USER_MESSAGES.scenarioInfoUnknown}
              </EmptyStateText>
            </EmptyState>
          )}
        </Stack>
      </Drawer>
    </>
  )
}
